// frontend/src/pages/Profile.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { updateProfile, changePassword, getUserById } from "../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import {
  faUserCircle,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,faBuilding,
  faSave,
  faKey,
  faCheckCircle,
  faExclamationTriangle,
  faArrowLeft,
  faCity,
  faGlobe,
  faTractor,
  faStore,
  faCreditCard,
  faWallet,
  faBell,
  faHistory,
  faSpinner,
  faEdit,
  faTimes,
  faPlus,
  faTrash,
  faShippingFast,
  faBox,
  faCalendarAlt,
  faUserCheck,
  faShieldAlt,
  faLock,
  faEye,
  faEyeSlash,
  faUsers,
  faInfoCircle,
  faUserTie,
  faCrown,
  faHandshake,
  faLightbulb,
  faClipboardList,
  faUserPlus,
  faUserCog,
  faBriefcase,
  faCode,
  faShieldAlt as faShieldAltIcon,
  faImage,
  faCamera,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../components/UI/Loader";
import "../styles/profile.scss";

// ============================================
// TEAM ROLE LABELS
// ============================================
const TEAM_ROLE_LABELS = {
  sale_representative: "Sales Representative",
  advisor: "Advisor",
  coordinator: "Coordinator",
  assistant_advisor: "Assistant Advisor",
  assistant_coordinator: "Assistant Coordinator",
  manager: "Manager",
  assistant_manager: "Assistant Manager",
  administrator: "Administrator",
  community_relation: "Community & Farmer Relation",
  developer: "Developer",
};

// ============================================
// MAIN COMPONENT
// ============================================
const Profile = () => {
  const { userId } = useParams();
  const { user, setUser, logout, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  // Refs for image uploads
  const profileInputRef = useRef(null);
  const backgroundInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [activeTab, setActiveTab] = useState("profile");
  const [showPassword, setShowPassword] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [isOwnProfile, setIsOwnProfile] = useState(true);

  // Image preview states
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState(null);
  const [companyBannerPreview, setCompanyBannerPreview] = useState(null);

  // Profile Form State
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    address: { street: "", city: "", district: "", country: "Sierra Leone" },
    preferredCurrency: "SLL",
    notificationsEnabled: true,
    emailNotifications: true,
    whatsappNotifications: true,
    profileImage: null,
    backgroundImage: null,
    companyLogo: null,
    companyBanner: null,
  });

  // Farmer-specific state
  const [farmData, setFarmData] = useState({
    farmDetails: {
      farmName: "",
      farmSize: "small",
      livestockType: [],
      animalCount: 0,
      yearsInOperation: 0,
    },
    bulkPurchaseEnabled: false,
    minimumBulkQuantity: 100,
    preferredPaymentMethod: "cash_on_delivery",
    creditLimit: 0,
    paymentTerms: 30,
  });

  // Password Change State
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Address Management
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  const [newAddress, setNewAddress] = useState({
    recipientName: "",
    street: "",
    city: "",
    district: "",
    country: "Sierra Leone",
    postalCode: "",
    phone: "",
    isDefault: false,
    label: "Home",
  });

  const isFarmer =
    profileUser?.userType === "farmer" || profileUser?.role === "farmer";
  const isSupplier =
    profileUser?.userType === "supplier" || profileUser?.role === "supplier";
  const canEdit = isOwnProfile || isAdmin() || isSuperAdmin();

  // ============================================
  // IMAGE HANDLERS
  // ============================================
  const handleImageUpload = (file, type) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      switch (type) {
        case "profile":
          setProfileImagePreview(base64String);
          setProfileData((prev) => ({ ...prev, profileImage: base64String }));
          break;
        case "background":
          setBackgroundImagePreview(base64String);
          setProfileData((prev) => ({
            ...prev,
            backgroundImage: base64String,
          }));
          break;
        case "logo":
          setCompanyLogoPreview(base64String);
          setProfileData((prev) => ({ ...prev, companyLogo: base64String }));
          break;
        case "banner":
          setCompanyBannerPreview(base64String);
          setProfileData((prev) => ({ ...prev, companyBanner: base64String }));
          break;
        default:
          break;
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (type) => {
    switch (type) {
      case "profile":
        setProfileImagePreview(null);
        setProfileData((prev) => ({ ...prev, profileImage: null }));
        if (profileInputRef.current) profileInputRef.current.value = "";
        break;
      case "background":
        setBackgroundImagePreview(null);
        setProfileData((prev) => ({ ...prev, backgroundImage: null }));
        if (backgroundInputRef.current) backgroundInputRef.current.value = "";
        break;
      case "logo":
        setCompanyLogoPreview(null);
        setProfileData((prev) => ({ ...prev, companyLogo: null }));
        if (logoInputRef.current) logoInputRef.current.value = "";
        break;
      case "banner":
        setCompanyBannerPreview(null);
        setProfileData((prev) => ({ ...prev, companyBanner: null }));
        if (bannerInputRef.current) bannerInputRef.current.value = "";
        break;
      default:
        break;
    }
  };

  const getUserAvatar = () =>
    profileImagePreview ||
    profileUser?.profileImage ||
    profileUser?.avatar ||
    null;
  const getBackgroundImage = () =>
    backgroundImagePreview || profileUser?.backgroundImage || null;
  const getCompanyLogo = () =>
    companyLogoPreview || profileUser?.companyLogo || null;
  const getCompanyBanner = () =>
    companyBannerPreview || profileUser?.companyBanner || null;

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        let targetUser = user;
        if (userId && userId !== user?._id) {
          try {
            const response = await getUserById(userId);
            if (response.data) {
              targetUser = response.data;
              setIsOwnProfile(false);
            } else throw new Error("User not found");
          } catch (err) {
            setMessage({
              type: "error",
              text: "User not found. Redirecting to your profile.",
            });
            setTimeout(() => navigate("/profile"), 2000);
            return;
          }
        } else setIsOwnProfile(true);

        setProfileUser(targetUser);
        setProfileData({
          name: targetUser.name || "",
          email: targetUser.email || "",
          phone: targetUser.phone || "",
          address: targetUser.address || {
            street: "",
            city: "",
            district: "",
            country: "Sierra Leone",
          },
          preferredCurrency: targetUser.preferredCurrency || "SLL",
          notificationsEnabled: targetUser.notificationsEnabled !== false,
          emailNotifications: targetUser.emailNotifications !== false,
          whatsappNotifications: targetUser.whatsappNotifications !== false,
          profileImage: targetUser.profileImage || null,
          backgroundImage: targetUser.backgroundImage || null,
          companyLogo: targetUser.companyLogo || null,
          companyBanner: targetUser.companyBanner || null,
        });

        if (targetUser.profileImage)
          setProfileImagePreview(targetUser.profileImage);
        if (targetUser.backgroundImage)
          setBackgroundImagePreview(targetUser.backgroundImage);
        if (targetUser.companyLogo)
          setCompanyLogoPreview(targetUser.companyLogo);
        if (targetUser.companyBanner)
          setCompanyBannerPreview(targetUser.companyBanner);

        if (targetUser.userType === "farmer" || targetUser.role === "farmer") {
          setFarmData({
            farmDetails: targetUser.farmDetails || {
              farmName: "",
              farmSize: "small",
              livestockType: [],
              animalCount: 0,
              yearsInOperation: 0,
            },
            bulkPurchaseEnabled: targetUser.bulkPurchaseEnabled || false,
            minimumBulkQuantity: targetUser.minimumBulkQuantity || 100,
            preferredPaymentMethod:
              targetUser.preferredPaymentMethod || "cash_on_delivery",
            creditLimit: targetUser.creditLimit || 0,
            paymentTerms: targetUser.paymentTerms || 30,
          });
        }
        setAddresses(targetUser.addresses || []);
      } catch (error) {
        console.error("Error loading profile:", error);
        setMessage({ type: "error", text: "Failed to load profile data" });
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [userId, user, navigate]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleProfileChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox")
      setProfileData((prev) => ({ ...prev, [name]: checked }));
    else if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setProfileData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFarmChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox")
      setFarmData((prev) => ({ ...prev, [name]: checked }));
    else if (name.startsWith("farmDetails.")) {
      const field = name.split(".")[1];
      setFarmData((prev) => ({
        ...prev,
        farmDetails: { ...prev.farmDetails, [field]: value },
      }));
    } else setFarmData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLivestockToggle = (type) => {
    const currentTypes = farmData.farmDetails.livestockType || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];
    setFarmData((prev) => ({
      ...prev,
      farmDetails: { ...prev.farmDetails, livestockType: newTypes },
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") setNewAddress({ ...newAddress, [name]: checked });
    else setNewAddress({ ...newAddress, [name]: value });
  };

  const addAddress = () => {
    if (
      !newAddress.recipientName ||
      !newAddress.street ||
      !newAddress.city ||
      !newAddress.district
    ) {
      setMessage({
        type: "error",
        text: "Please fill in all required address fields",
      });
      return;
    }
    const updatedAddresses = [...addresses];
    if (newAddress.isDefault)
      updatedAddresses.forEach((addr) => (addr.isDefault = false));
    if (editingAddressIndex !== null) {
      updatedAddresses[editingAddressIndex] = { ...newAddress };
      setEditingAddressIndex(null);
    } else updatedAddresses.push({ ...newAddress });
    setAddresses(updatedAddresses);
    setNewAddress({
      recipientName: "",
      street: "",
      city: "",
      district: "",
      country: "Sierra Leone",
      postalCode: "",
      phone: "",
      isDefault: addresses.length === 0,
      label: "Home",
    });
    setShowAddressForm(false);
  };

  const editAddress = (index) => {
    setNewAddress({ ...addresses[index] });
    setEditingAddressIndex(index);
    setShowAddressForm(true);
  };

  const removeAddress = (index) => {
    const updatedAddresses = addresses.filter((_, i) => i !== index);
    if (addresses[index]?.isDefault && updatedAddresses.length > 0)
      updatedAddresses[0].isDefault = true;
    setAddresses(updatedAddresses);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      setMessage({ type: "error", text: "You don't have permission to edit" });
      return;
    }
    setUpdating(true);
    setMessage({ type: "", text: "" });

    try {
      const updateData = {
        name: profileData.name,
        phone: profileData.phone,
        address: profileData.address,
        preferredCurrency: profileData.preferredCurrency,
        notificationsEnabled: profileData.notificationsEnabled,
        emailNotifications: profileData.emailNotifications,
        whatsappNotifications: profileData.whatsappNotifications,
        profileImage: profileData.profileImage,
        backgroundImage: profileData.backgroundImage,
        companyLogo: profileData.companyLogo,
        companyBanner: profileData.companyBanner,
        addresses: addresses,
      };
      if (isFarmer) {
        updateData.farmDetails = farmData.farmDetails;
        updateData.bulkPurchaseEnabled = farmData.bulkPurchaseEnabled;
        updateData.minimumBulkQuantity = farmData.minimumBulkQuantity;
        updateData.preferredPaymentMethod = farmData.preferredPaymentMethod;
        updateData.creditLimit = farmData.creditLimit;
        updateData.paymentTerms = farmData.paymentTerms;
      }

      const response = await updateProfile(updateData);
      if (response.data.success) {
        const updatedUser = { ...user, ...updateData };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        if (setUser) setUser(updatedUser);
        setProfileUser(updatedUser);
        setMessage({ type: "success", text: "Profile updated successfully!" });
        setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to update profile",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!isOwnProfile) {
      setMessage({
        type: "error",
        text: "You can only change your own password",
      });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setMessage({
        type: "error",
        text: "Password must be at least 6 characters",
      });
      return;
    }

    setUpdating(true);
    setMessage({ type: "", text: "" });
    try {
      await changePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
      );
      setMessage({
        type: "success",
        text: "Password changed successfully! Please login again.",
      });
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => {
        logout();
        navigate("/login");
      }, 2000);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to change password",
      });
    } finally {
      setUpdating(false);
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleDisplay = () => {
    if (profileUser?.role === "super_admin") return "Super Admin";
    if (profileUser?.role === "admin") return "Administrator";
    if (profileUser?.isTeamMember)
      return TEAM_ROLE_LABELS[profileUser.teamRole] || "Team Member";
    if (profileUser?.userType === "farmer") return "Farmer";
    if (profileUser?.userType === "supplier") return "Supplier";
    return "Customer";
  };

  const getRoleIcon = () => {
    if (profileUser?.role === "super_admin") return faCrown;
    if (profileUser?.role === "admin") return faShieldAltIcon;
    if (profileUser?.isTeamMember) {
      const icons = {
        sale_representative: faHandshake,
        advisor: faLightbulb,
        coordinator: faClipboardList,
        assistant_advisor: faUserPlus,
        assistant_coordinator: faUserCog,
        manager: faBriefcase,
        assistant_manager: faUserTie,
        administrator: faShieldAltIcon,
        community_relation: faUsers,
        developer: faCode,
      };
      return icons[profileUser.teamRole] || faUserTie;
    }
    if (profileUser?.userType === "farmer") return faTractor;
    if (profileUser?.userType === "supplier") return faStore;
    return faUserCircle;
  };

  const livestockOptions = [
    "Cattle",
    "Poultry",
    "Goats",
    "Sheep",
    "Pigs",
    "Mixed",
  ];
  const farmSizeOptions = [
    { value: "small", label: "Small (1-50 animals)" },
    { value: "medium", label: "Medium (51-200 animals)" },
    { value: "large", label: "Large (200+ animals)" },
  ];
  const paymentMethodOptions = [
    { value: "cash_on_delivery", label: "Cash on Delivery" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "orange_money", label: "Orange Money" },
    { value: "afrimoney", label: "Afrimoney" },
    { value: "wave", label: "WAVE" },
    { value: "credit", label: "Credit (30 days)" },
  ];
  const currencyOptions = [
    { value: "SLL", label: "SLL (Le)" },
    { value: "USD", label: "USD ($)" },
    { value: "MYR", label: "MYR (RM)" },
  ];

  const tabs = [
    { id: "profile", label: "Profile", icon: faUserCircle },
    ...(isOwnProfile
      ? [{ id: "security", label: "Security", icon: faKey }]
      : []),
    ...(isFarmer ? [{ id: "farm", label: "Farm", icon: faTractor }] : []),
    { id: "addresses", label: "Addresses", icon: faMapMarkerAlt },
    { id: "preferences", label: "Preferences", icon: faBell },
  ];

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader />;

  return (
    <motion.div
      className="profile-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="profile-container">
        {/* Header */}
        <div className="profile-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          <div className="header-content">
            <div className="profile-avatar">
              {getUserAvatar() ? (
                <img src={getUserAvatar()} alt={profileUser?.name} />
              ) : (
                <span className="avatar-initials">
                  {getInitials(profileUser?.name)}
                </span>
              )}
            </div>
            <div>
              <h1>{profileUser?.name || "User Profile"}</h1>
              <p className="profile-role">
                <FontAwesomeIcon icon={getRoleIcon()} />
                {getRoleDisplay()}
                {!isOwnProfile && (
                  <span className="viewing-badge">
                    <FontAwesomeIcon icon={faEye} /> Viewing
                  </span>
                )}
                {isAdmin() && !isOwnProfile && (
                  <span className="admin-badge">
                    <FontAwesomeIcon icon={faShieldAltIcon} /> Admin View
                  </span>
                )}
              </p>
            </div>
          </div>
          {isOwnProfile && (
            <div className="header-actions">
              <button
                className="logout-btn"
                onClick={() => {
                  if (window.confirm("Are you sure you want to logout?")) {
                    logout();
                    navigate("/login");
                  }
                }}
              >
                <FontAwesomeIcon icon={faTimes} /> Logout
              </button>
            </div>
          )}
        </div>

        {/* Message Alert */}
        <AnimatePresence>
          {message.text && (
            <motion.div
              className={`profile-alert ${message.type}`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <FontAwesomeIcon
                icon={
                  message.type === "success"
                    ? faCheckCircle
                    : faExclamationTriangle
                }
              />
              <span>{message.text}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className="profile-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={activeTab === tab.id ? "active" : ""}
              onClick={() => setActiveTab(tab.id)}
            >
              <FontAwesomeIcon icon={tab.icon} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="profile-grid">
          {/* Profile Tab */}
          {activeTab === "profile" && (
            <motion.div
              className="profile-card full-width"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>
                <FontAwesomeIcon icon={faUserCircle} /> Personal Information
              </h2>
              <form onSubmit={handleProfileSubmit} className="profile-form">
                {/* Profile Image Upload */}
                <div className="form-group image-upload-group">
                  <label>
                    <FontAwesomeIcon icon={faImage} /> Profile Image
                  </label>
                  <div className="image-upload-wrapper">
                    <div
                      className="image-preview-circle"
                      onClick={() => profileInputRef.current?.click()}
                      style={{ cursor: canEdit ? "pointer" : "default" }}
                    >
                      {getUserAvatar() ? (
                        <>
                          <img src={getUserAvatar()} alt="Profile" />
                          {canEdit && (
                            <button
                              type="button"
                              className="image-remove-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage("profile");
                              }}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="image-placeholder">
                          <FontAwesomeIcon icon={faCamera} />
                          <span>Upload</span>
                        </div>
                      )}
                    </div>
                    <input
                      ref={profileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files?.[0])
                          handleImageUpload(e.target.files[0], "profile");
                        e.target.value = "";
                      }}
                      style={{ display: "none" }}
                      disabled={!canEdit}
                    />
                    <small>
                      {canEdit
                        ? "Click to upload a profile photo"
                        : "Profile photo"}
                    </small>
                  </div>
                </div>

                {/* Background Image Upload */}
                <div className="form-group image-upload-group">
                  <label>
                    <FontAwesomeIcon icon={faImage} /> Background Image
                  </label>
                  <div className="image-upload-wrapper">
                    <div
                      className="image-preview-banner"
                      onClick={() => backgroundInputRef.current?.click()}
                      style={{ cursor: canEdit ? "pointer" : "default" }}
                    >
                      {getBackgroundImage() ? (
                        <>
                          <img src={getBackgroundImage()} alt="Background" />
                          {canEdit && (
                            <button
                              type="button"
                              className="image-remove-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage("background");
                              }}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="image-placeholder-text">
                          <FontAwesomeIcon icon={faUpload} /> Upload background
                          image
                        </span>
                      )}
                    </div>
                    <input
                      ref={backgroundInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files?.[0])
                          handleImageUpload(e.target.files[0], "background");
                        e.target.value = "";
                      }}
                      style={{ display: "none" }}
                      disabled={!canEdit}
                    />
                  </div>
                </div>

                {/* Supplier Logo & Banner */}
                {isSupplier && (
                  <>
                    <div className="form-group image-upload-group">
                      <label>
                        <FontAwesomeIcon icon={faBuilding} /> Company Logo
                      </label>
                      <div className="image-upload-wrapper">
                        <div
                          className="image-preview-square"
                          onClick={() => logoInputRef.current?.click()}
                          style={{ cursor: canEdit ? "pointer" : "default" }}
                        >
                          {getCompanyLogo() ? (
                            <>
                              <img src={getCompanyLogo()} alt="Company Logo" />
                              {canEdit && (
                                <button
                                  type="button"
                                  className="image-remove-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveImage("logo");
                                  }}
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              )}
                            </>
                          ) : (
                            <div className="image-placeholder">
                              <FontAwesomeIcon icon={faBuilding} />
                              <span>Logo</span>
                            </div>
                          )}
                        </div>
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files?.[0])
                              handleImageUpload(e.target.files[0], "logo");
                            e.target.value = "";
                          }}
                          style={{ display: "none" }}
                          disabled={!canEdit}
                        />
                        <small>
                          {canEdit
                            ? "Click to upload company logo"
                            : "Company logo"}
                        </small>
                      </div>
                    </div>

                    <div className="form-group image-upload-group">
                      <label>
                        <FontAwesomeIcon icon={faImage} /> Company Banner
                      </label>
                      <div className="image-upload-wrapper">
                        <div
                          className="image-preview-banner"
                          onClick={() => bannerInputRef.current?.click()}
                          style={{ cursor: canEdit ? "pointer" : "default" }}
                        >
                          {getCompanyBanner() ? (
                            <>
                              <img
                                src={getCompanyBanner()}
                                alt="Company Banner"
                              />
                              {canEdit && (
                                <button
                                  type="button"
                                  className="image-remove-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveImage("banner");
                                  }}
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              )}
                            </>
                          ) : (
                            <span className="image-placeholder-text">
                              <FontAwesomeIcon icon={faUpload} /> Upload company
                              banner
                            </span>
                          )}
                        </div>
                        <input
                          ref={bannerInputRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files?.[0])
                              handleImageUpload(e.target.files[0], "banner");
                            e.target.value = "";
                          }}
                          style={{ display: "none" }}
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faUserCircle} /> Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={profileData.name}
                      onChange={handleProfileChange}
                      required
                      placeholder="Enter your full name"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faEnvelope} /> Email Address
                    </label>
                    <input
                      type="email"
                      value={profileData.email}
                      disabled
                      className="disabled-input"
                    />
                    <small>
                      Email cannot be changed. Contact support for assistance.
                    </small>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faPhone} /> Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                    placeholder="Enter your phone number"
                    disabled={!canEdit}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> Street Address
                  </label>
                  <input
                    type="text"
                    name="address.street"
                    value={profileData.address?.street || ""}
                    onChange={handleProfileChange}
                    placeholder="Enter your street address"
                    disabled={!canEdit}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faCity} /> City
                    </label>
                    <input
                      type="text"
                      name="address.city"
                      value={profileData.address?.city || ""}
                      onChange={handleProfileChange}
                      placeholder="Enter your city"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="form-group">
                    <label>District</label>
                    <input
                      type="text"
                      name="address.district"
                      value={profileData.address?.district || ""}
                      onChange={handleProfileChange}
                      placeholder="Enter your district"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faGlobe} /> Country
                    </label>
                    <input
                      type="text"
                      name="address.country"
                      value={profileData.address?.country || "Sierra Leone"}
                      onChange={handleProfileChange}
                      placeholder="Enter your country"
                      disabled={!canEdit}
                    />
                  </div>
                </div>

                {canEdit && (
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={updating}
                  >
                    <FontAwesomeIcon icon={faSave} />{" "}
                    {updating ? "Saving..." : "Save Changes"}
                  </button>
                )}
                {!canEdit && (
                  <div className="readonly-notice">
                    <FontAwesomeIcon icon={faInfoCircle} /> You are viewing this
                    profile in read-only mode.
                  </div>
                )}
              </form>
            </motion.div>
          )}

          {/* Security Tab */}
          {activeTab === "security" && isOwnProfile && (
            <motion.div
              className="profile-card full-width"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>
                <FontAwesomeIcon icon={faKey} /> Security
              </h2>
              <form onSubmit={handlePasswordChange} className="password-form">
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        currentPassword: e.target.value,
                      })
                    }
                    required
                    placeholder="Enter your current password"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            newPassword: e.target.value,
                          })
                        }
                        required
                        placeholder="Enter new password (min 6 characters)"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <FontAwesomeIcon
                          icon={showPassword ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          confirmPassword: e.target.value,
                        })
                      }
                      required
                      placeholder="Confirm your new password"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={updating}
                >
                  <FontAwesomeIcon icon={faKey} />{" "}
                  {updating ? "Changing..." : "Change Password"}
                </button>
              </form>
              <div className="security-tips">
                <h4>
                  <FontAwesomeIcon icon={faShieldAlt} /> Security Tips
                </h4>
                <ul>
                  <li>Use a strong password with at least 8 characters</li>
                  <li>Include numbers, symbols, and mixed case letters</li>
                  <li>Never share your password with anyone</li>
                  <li>Enable two-factor authentication for added security</li>
                </ul>
              </div>
            </motion.div>
          )}

          {/* Farm Tab */}
          {activeTab === "farm" && isFarmer && (
            <motion.div
              className="profile-card full-width"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>
                <FontAwesomeIcon icon={faTractor} /> Farm Details
              </h2>
              <form onSubmit={handleProfileSubmit} className="profile-form">
                <div className="form-group">
                  <label>Farm/Business Name</label>
                  <input
                    type="text"
                    name="farmDetails.farmName"
                    value={farmData.farmDetails.farmName}
                    onChange={handleFarmChange}
                    placeholder="Enter your farm name"
                    disabled={!canEdit}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Farm Size</label>
                    <select
                      name="farmDetails.farmSize"
                      value={farmData.farmDetails.farmSize}
                      onChange={handleFarmChange}
                      disabled={!canEdit}
                    >
                      {farmSizeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Animal Count</label>
                    <input
                      type="number"
                      name="farmDetails.animalCount"
                      value={farmData.farmDetails.animalCount}
                      onChange={handleFarmChange}
                      min="0"
                      placeholder="Number of animals"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Livestock Type</label>
                  <div className="livestock-tags">
                    {livestockOptions.map((type) => (
                      <button
                        key={type}
                        type="button"
                        className={`livestock-tag ${farmData.farmDetails.livestockType?.includes(type) ? "active" : ""}`}
                        onClick={() => handleLivestockToggle(type)}
                        disabled={!canEdit}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Years in Operation</label>
                    <input
                      type="number"
                      name="farmDetails.yearsInOperation"
                      value={farmData.farmDetails.yearsInOperation}
                      onChange={handleFarmChange}
                      min="0"
                      placeholder="Years in operation"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="form-group">
                    <label>Preferred Payment Method</label>
                    <select
                      name="preferredPaymentMethod"
                      value={farmData.preferredPaymentMethod}
                      onChange={handleFarmChange}
                      disabled={!canEdit}
                    >
                      {paymentMethodOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="bulkPurchaseEnabled"
                        checked={farmData.bulkPurchaseEnabled}
                        onChange={handleFarmChange}
                        disabled={!canEdit}
                      />
                      Enable bulk purchases
                    </label>
                  </div>
                  <div className="form-group">
                    <label>Minimum Bulk Quantity (kg)</label>
                    <input
                      type="number"
                      name="minimumBulkQuantity"
                      value={farmData.minimumBulkQuantity}
                      onChange={handleFarmChange}
                      min="0"
                      placeholder="Minimum bulk quantity"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Credit Limit (USD)</label>
                    <input
                      type="number"
                      name="creditLimit"
                      value={farmData.creditLimit}
                      onChange={handleFarmChange}
                      min="0"
                      step="0.01"
                      placeholder="Credit limit"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Terms (days)</label>
                    <input
                      type="number"
                      name="paymentTerms"
                      value={farmData.paymentTerms}
                      onChange={handleFarmChange}
                      min="0"
                      placeholder="Payment terms in days"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
                {canEdit && (
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={updating}
                  >
                    <FontAwesomeIcon icon={faSave} />{" "}
                    {updating ? "Saving..." : "Save Farm Details"}
                  </button>
                )}
              </form>
            </motion.div>
          )}

          {/* Addresses Tab */}
          {activeTab === "addresses" && (
            <motion.div
              className="profile-card full-width"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Addresses
              </h2>
              <div className="addresses-list">
                {addresses.map((address, index) => (
                  <div key={index} className="address-item">
                    <div className="address-info">
                      <div className="address-label">
                        {address.label || "Address"}
                        {address.isDefault && (
                          <span className="default-badge">Default</span>
                        )}
                      </div>
                      <div className="address-details">
                        {address.recipientName}
                        <br />
                        {address.street}
                        <br />
                        {address.city}, {address.district}
                        <br />
                        {address.country}
                        {address.phone && (
                          <>
                            <br />
                            <FontAwesomeIcon icon={faPhone} /> {address.phone}
                          </>
                        )}
                      </div>
                    </div>
                    {canEdit && (
                      <div className="address-actions">
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => editAddress(index)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => removeAddress(index)}
                          disabled={addresses.length <= 1}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {canEdit && !showAddressForm ? (
                <button
                  type="button"
                  className="add-address-btn"
                  onClick={() => {
                    setShowAddressForm(true);
                    setEditingAddressIndex(null);
                    setNewAddress({
                      recipientName: profileData.name || "",
                      street: "",
                      city: "",
                      district: "",
                      country: "Sierra Leone",
                      postalCode: "",
                      phone: profileData.phone || "",
                      isDefault: addresses.length === 0,
                      label: "Home",
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Address
                </button>
              ) : (
                canEdit && (
                  <div className="address-form">
                    <h4>
                      {editingAddressIndex !== null
                        ? "Edit Address"
                        : "Add New Address"}
                    </h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Recipient Name *</label>
                        <input
                          type="text"
                          name="recipientName"
                          value={newAddress.recipientName}
                          onChange={handleAddressChange}
                          placeholder="Recipient name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Label</label>
                        <input
                          type="text"
                          name="label"
                          value={newAddress.label}
                          onChange={handleAddressChange}
                          placeholder="e.g., Home, Office"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Street Address *</label>
                      <input
                        type="text"
                        name="street"
                        value={newAddress.street}
                        onChange={handleAddressChange}
                        placeholder="Street address"
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>City *</label>
                        <input
                          type="text"
                          name="city"
                          value={newAddress.city}
                          onChange={handleAddressChange}
                          placeholder="City"
                        />
                      </div>
                      <div className="form-group">
                        <label>District *</label>
                        <input
                          type="text"
                          name="district"
                          value={newAddress.district}
                          onChange={handleAddressChange}
                          placeholder="District"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Country</label>
                        <input
                          type="text"
                          name="country"
                          value={newAddress.country}
                          onChange={handleAddressChange}
                          placeholder="Country"
                        />
                      </div>
                      <div className="form-group">
                        <label>Postal Code</label>
                        <input
                          type="text"
                          name="postalCode"
                          value={newAddress.postalCode}
                          onChange={handleAddressChange}
                          placeholder="Postal code"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={newAddress.phone}
                        onChange={handleAddressChange}
                        placeholder="Phone number"
                      />
                    </div>
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="isDefault"
                          checked={newAddress.isDefault}
                          onChange={handleAddressChange}
                        />{" "}
                        Set as default address
                      </label>
                    </div>
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          setShowAddressForm(false);
                          setEditingAddressIndex(null);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={addAddress}
                      >
                        <FontAwesomeIcon icon={faSave} /> Save Address
                      </button>
                    </div>
                  </div>
                )
              )}
            </motion.div>
          )}

          {/* Preferences Tab */}
          {activeTab === "preferences" && (
            <motion.div
              className="profile-card full-width"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>
                <FontAwesomeIcon icon={faBell} /> Preferences
              </h2>
              <form onSubmit={handleProfileSubmit} className="profile-form">
                <div className="form-group">
                  <label>Preferred Currency</label>
                  <select
                    name="preferredCurrency"
                    value={profileData.preferredCurrency}
                    onChange={handleProfileChange}
                    disabled={!canEdit}
                  >
                    {currencyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="notificationsEnabled"
                        checked={profileData.notificationsEnabled}
                        onChange={handleProfileChange}
                        disabled={!canEdit}
                      />
                      <span>
                        <FontAwesomeIcon icon={faBell} /> Enable Notifications
                      </span>
                      <small>
                        Receive notifications from Kenali Enterprise
                      </small>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="emailNotifications"
                        checked={profileData.emailNotifications}
                        onChange={handleProfileChange}
                        disabled={!canEdit}
                      />
                      <span>
                        <FontAwesomeIcon icon={faEnvelope} /> Email
                        Notifications
                      </span>
                      <small>Receive email notifications</small>
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="whatsappNotifications"
                      checked={profileData.whatsappNotifications}
                      onChange={handleProfileChange}
                      disabled={!canEdit}
                    />
                    <span>
                      <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp
                      Notifications
                    </span>
                    <small>Receive WhatsApp notifications</small>
                  </label>
                </div>
                {canEdit && (
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={updating}
                  >
                    <FontAwesomeIcon icon={faSave} />{" "}
                    {updating ? "Saving..." : "Save Preferences"}
                  </button>
                )}
              </form>
            </motion.div>
          )}
        </div>

        {/* Account Information */}
        <div className="profile-card account-info">
          <h2>Account Information</h2>
          <div className="account-details">
            <div className="account-detail">
              <span className="detail-label">Account Type:</span>
              <span className="detail-value">{getRoleDisplay()}</span>
            </div>
            <div className="account-detail">
              <span className="detail-label">Member Since:</span>
              <span className="detail-value">
                {profileUser?.createdAt
                  ? new Date(profileUser.createdAt).toLocaleDateString()
                  : "N/A"}
              </span>
            </div>
            <div className="account-detail">
              <span className="detail-label">Account Status:</span>
              <span
                className={`status-badge ${profileUser?.isActive !== false ? "active" : "inactive"}`}
              >
                {profileUser?.isActive !== false ? "Active" : "Inactive"}
              </span>
            </div>
            {isOwnProfile && (
              <div className="account-detail">
                <span className="detail-label">Login Count:</span>
                <span className="detail-value">
                  {profileUser?.loginCount || 0}
                </span>
              </div>
            )}
            {isOwnProfile && profileUser?.lastLogin && (
              <div className="account-detail">
                <span className="detail-label">Last Login:</span>
                <span className="detail-value">
                  {new Date(profileUser.lastLogin).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Profile;
